/**
 * Inventory of every rule in the app that can behave differently on a touch
 * device, and the gate each one sits behind. Grep `TOUCH-ONLY` to check this
 * list is complete:
 *
 * ```
 * grep -rn "TOUCH-ONLY" apps libs
 * ```
 *
 * Every rule sits behind TWO gates: the `FF_TouchUi_Root` feature flag, which the
 * console's `TouchUiGate` writes to `<html data-touch-ui>` (see
 * `utils.touchUi.ts`), and a pointer check. CSS rules are
 * `:where([data-touch-ui]) .x` inside the media query; `:where()` keeps their
 * specificity unchanged. JS rules go through `useIsCoarsePointer`, which ANDs
 * the attribute with the media query. Flag off = the attribute is absent =
 * production before FUS-1196, on every device.
 *
 * | Where | Gate |
 * | -- | -- |
 * | `utils.touch.constants.ts`, this file | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `toolbar.css`, button min-width | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `toolbar.css`, horizontal scroll | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `panel.css`, panel width | `[data-touch-ui]` + phone query |
 * | `widget.css`, right rail scroll | `[data-touch-ui]` + phone query |
 * | `map.shell.css`, safe areas | `env()`, 0 without insets (not flagged: inert without insets) |
 * | `dialog.css`, dialog capped to the viewport and scrollable | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `dropdownMenu.css`, menu capped to the width Radix has room for | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `popover.css`, popover capped to the width Radix has room for | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `utils.mobile.tsx`, `useIsMobile` (full-width popovers, sidebar sheet) | width < 768 px for a mouse, short edge < 500 px only when `useIsCoarsePointer` |
 * | `Popover.tsx`, bottom-of-viewport anchor | `useIsMobile` and not `useIsCoarsePointer` |
 * | `MapWidgetManager.tsx`, the < 768 px widget gate | skipped only when `useIsCoarsePointer` |
 * | `MapWidgetToolbarRight.tsx`, height floor | `useIsCoarsePointer` and `useHasTabletViewport` |
 * | `MapWidgetBookmarkListItem.tsx`, row menu always visible (`TOUCH_REVEAL_CLASS`) | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `MapActiveLayerMenuItem.tsx`, row menu always visible (`TOUCH_REVEAL_CLASS`) | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `LegalPlatformCadastreListItem.tsx`, zoom and remove always visible (`TOUCH_REVEAL_CLASS`) | `[data-touch-ui]` + `@media (pointer: coarse)` |
 * | `MapSidebar.tsx`, invite and help join the overflow list on the phone tier | `useIsCoarsePointer` and not `useHasTabletViewport` |
 * | `ObjectSharePercentage.tsx`, a tap opens the share fraction tooltip (andel owners in the owner tree, transaction parties) | `useIsCoarsePointer` |
 *
 * The phone query is `(pointer: coarse) and (max-width: 499px), (pointer:
 * coarse) and (max-height: 499px)`: a coarse pointer on a viewport whose short
 * edge is under 500 px, the inverse of `useHasTabletViewport`. A phone matches
 * whichever way it is held, no tablet matches, and a mouse never matches
 * whatever the window size. `env()` resolves to 0 where there are no safe
 * areas.
 *
 * Together that is why none of this reaches a Mac or a PC. It is also checked
 * by measurement: the branch is compared against production in Chrome at
 * 1440x900, 1680x1050, 2560x1440 and 1280x800 on every change.
 *
 * The rules deliberately live beside the component each one overrides, matching
 * how the rest of the CSS in these libraries is organized, rather than in one
 * device stylesheet whose effect would depend on import order.
 */

/** Attribute on `<html>` that allows the touch UI. Written only by `setTouchUiEnabled`. */
export const TOUCH_UI_ATTRIBUTE = "data-touch-ui"

/**
 * TOUCH-ONLY (FUS-1196). Minimum hit area on coarse pointers: 44 px, the figure Apple's HIG gives for
 * a finger. Written as a media query rather than a device check, so a mouse
 * keeps the dense desktop sizes and a touchscreen laptop gets whichever
 * pointer is actually primary. The `[data-touch-ui]_&` variant is the feature
 * flag gate; both variants must match.
 *
 * `min-height` / `min-width` rather than `height`, so a control grows into the
 * target without its padding, font size, or icon changing. Applied by
 * `buttonBaseVariants` to every size below 44 px and by the sidebar nav rows.
 *
 * See FUS-1196.
 */
export const TOUCH_TARGET_CLASS =
  "[@media(pointer:coarse)]:[[data-touch-ui]_&]:min-h-11 [@media(pointer:coarse)]:[[data-touch-ui]_&]:min-w-11"

/**
 * TOUCH-ONLY (FUS-1196). A control that reveals itself on hover (`opacity-0
 * group-hover:opacity-100`, often with `pointer-events-none`) has no entry
 * point for a finger: no iPad reports `hover: hover`, so the row menu that
 * renames or deletes a bookmark could never be opened. On a coarse pointer the
 * control is simply always shown. Append to the reveal wrapper's class list;
 * the mouse path keeps its hover reveal untouched.
 *
 * See FUS-1196.
 */
export const TOUCH_REVEAL_CLASS =
  "[@media(pointer:coarse)]:[[data-touch-ui]_&]:opacity-100 [@media(pointer:coarse)]:[[data-touch-ui]_&]:pointer-events-auto"

/**
 * The same reveal for a wrapper that is `hidden` (not transparent) until
 * hover, so the hidden control takes no width on a mouse. On a coarse
 * pointer the wrapper is simply always laid out. Pair with
 * `group-hover/…:flex` on the mouse path.
 */
export const TOUCH_REVEAL_DISPLAY_CLASS =
  "[@media(pointer:coarse)]:[[data-touch-ui]_&]:flex"
